import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, MessageCircle, BookOpen, FileText, Building2, Shield, Calculator, Scale, Users, ClipboardList } from 'lucide-react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { API } from '../App';

gsap.registerPlugin(ScrollTrigger);

const PRIMARY = '#3B5FE0';
const ACCENT = '#F4A340';
const BG = '#0F1420';

const SCENES = [
  { image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/7c9fe7442a1f3b6203f5afc7c9653d6a1fbe930feb54374a5249d0311d459783.jpeg", caption: "", pan: { scale: 1.25, x: -25, y: -12 } },
  { image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/2a9d5110986ad3b4f62a2687d772b2125fa09a34bae7e207f714644e1971c326.jpeg", caption: "Las facturas se acumulan...", pan: { scale: 1.2, x: 20, y: -8 } },
  { image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/610a0cae77ae25b455e1534a72052bf0514e369fb2325a826cbd1eb3b444eec5.jpeg", caption: "Pero hay orden en el caos.", pan: { scale: 1.22, x: -18, y: 10 } },
  { image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/c51d1ae7bdb9feb34f0f05a597625f5e523f3f134b719ac28efd04004c5abe1d.jpeg", caption: "Tu asesor te guía paso a paso.", pan: { scale: 1.18, x: 25, y: -15 } },
  { image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/ac9eb72a0032f03767c5c4ceba82cad130bca4b829576a6f458deb0e20ed1174.jpeg", caption: "Declaras con confianza.", pan: { scale: 1.25, x: -22, y: 12 } },
  { image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/20fccfca6ada7e51ea633aa09ea906b169d14d52cb390eabe115acc7ee8ac76f.jpeg", caption: "Tranquilidad tributaria.", pan: { scale: 1.2, x: 15, y: -10 } },
];

// ==================== CINEMATIC SCROLL ====================
const CinematicScroll = () => {
  const sectionRef = useRef(null);
  useGSAP(() => {
    if (!sectionRef.current) return;
    const sceneEls = gsap.utils.toArray('.t-cinema-scene');
    const captionEls = gsap.utils.toArray('.t-cinema-caption');
    const progressFill = sectionRef.current.querySelector('.t-cinema-progress-fill');
    if (!sceneEls.length) return;
    const isMobile = window.innerWidth < 768;
    const panScale = isMobile ? 0.45 : 1;
    const scrollDistance = isMobile ? 4000 : 7000;
    gsap.set(sceneEls.slice(1), { opacity: 0 });
    gsap.set(captionEls, { opacity: 0, y: isMobile ? 30 : 50 });
    const SLOT = 1;
    const tl = gsap.timeline({ scrollTrigger: { trigger: sectionRef.current, start: 'top top', end: `+=${scrollDistance}`, scrub: isMobile ? 0.3 : 0.5, pin: true, anticipatePin: 1, onUpdate: (self) => { if (progressFill) gsap.set(progressFill, { scaleY: self.progress }); } } });
    sceneEls.forEach((sceneEl, i) => {
      const img = sceneEl.querySelector('.t-cinema-img');
      if (!img) return;
      const start = i * SLOT;
      const isFirst = i === 0;
      const isLast = i === sceneEls.length - 1;
      const { scale: endScale, x: endX, y: endY } = SCENES[i].pan;
      if (!isFirst) tl.fromTo(sceneEl, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'power2.inOut' }, start - 0.1);
      tl.fromTo(img, { scale: 1, x: 0, y: 0 }, { scale: 1 + (endScale - 1) * panScale, x: endX * panScale, y: endY * panScale, duration: SLOT, ease: 'none' }, start);
      if (SCENES[i].caption) tl.fromTo(captionEls[i], { opacity: 0, y: isMobile ? 30 : 50 }, { opacity: 1, y: 0, duration: 0.12, ease: 'power3.out' }, start + 0.12);
      if (SCENES[i].caption && !isLast) tl.to(captionEls[i], { opacity: 0, y: isMobile ? -15 : -30, duration: 0.1, ease: 'power2.in' }, start + SLOT - 0.22);
      if (!isLast) tl.to(sceneEl, { opacity: 0, duration: 0.2, ease: 'power2.inOut' }, start + SLOT - 0.1);
    });
  }, { scope: sectionRef });

  return (
    <section ref={sectionRef} className="relative h-screen w-full overflow-hidden" style={{ background: BG }} data-testid="tributaria-cinematic-scroll">
      {SCENES.map((scene, i) => (
        <div key={i} className="t-cinema-scene absolute inset-0 overflow-hidden">
          <img src={scene.image} alt="" className="t-cinema-img absolute inset-0 w-full h-full object-cover" style={{ transformOrigin: 'center center' }} />
        </div>
      ))}
      <div className="absolute inset-0 pointer-events-none z-[2]" style={{ background: `linear-gradient(to bottom, ${BG}dd 0%, transparent 22%, transparent 55%, ${BG}ee 100%), radial-gradient(ellipse at center, transparent 40%, ${BG}88 100%)` }} />
      <div className="absolute bottom-12 sm:bottom-16 md:bottom-24 inset-x-0 z-[3] pointer-events-none px-4 sm:px-6">
        {SCENES.map((scene, i) => (
          <p key={i} className="t-cinema-caption absolute inset-x-0 text-center text-base sm:text-xl md:text-3xl lg:text-4xl text-white/90 font-light tracking-wide max-w-3xl mx-auto" style={{ fontFamily: 'Playfair Display, serif', textShadow: '0 4px 40px rgba(0,0,0,0.95)' }}>{scene.caption}</p>
        ))}
      </div>
      <div className="absolute right-3 sm:right-6 md:right-10 top-1/2 -translate-y-1/2 z-[4]">
        <div className="w-[2px] h-20 sm:h-28 rounded-full relative overflow-hidden" style={{ background: `${PRIMARY}30` }}>
          <div className="t-cinema-progress-fill absolute bottom-0 left-0 w-full h-full rounded-full origin-bottom" style={{ transform: 'scaleY(0)', background: PRIMARY }} />
        </div>
      </div>
    </section>
  );
};

const CinematicScrollWrapper = () => {
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let loaded = 0;
    const promises = SCENES.map(s => new Promise(r => { const img = new Image(); img.onload = img.onerror = () => { loaded++; setProgress(loaded / SCENES.length); r(); }; img.src = s.image; }));
    Promise.all(promises).then(() => setReady(true));
  }, []);
  if (!ready) return (
    <div className="h-screen flex items-center justify-center" style={{ background: BG }}>
      <div className="text-center">
        <div className="w-48 h-[2px] rounded-full overflow-hidden mb-4" style={{ background: `${PRIMARY}30` }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress * 100}%`, background: PRIMARY }} />
        </div>
        <p className="text-gray-600 text-[11px] tracking-[0.25em] uppercase">Cargando</p>
      </div>
    </div>
  );
  return <CinematicScroll />;
};

const FadeSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }} className={className}>{children}</motion.div>;
};

const SERVICIOS = [
  { icon: Calculator, title: 'Contabilidad general', desc: 'Registro y control de tus operaciones financieras con claridad y orden.' },
  { icon: FileText, title: 'Impuestos y tributación', desc: 'Declaraciones, planeación fiscal y cumplimiento ante la DIAN sin sorpresas.' },
  { icon: Users, title: 'Nómina y seguridad social', desc: 'Liquidación de nómina, aportes y todo lo relacionado con tu equipo de trabajo.' },
  { icon: Building2, title: 'Constitución y formalización', desc: 'Te acompañamos en crear tu empresa de forma legal y eficiente.' },
  { icon: Shield, title: 'Revisoría fiscal y auditoría', desc: 'Transparencia y confianza en cada cifra de tu organización.' },
  { icon: Scale, title: 'Consultoría financiera', desc: 'Asesoría estratégica para tomar mejores decisiones con tu dinero.' },
  { icon: ClipboardList, title: 'Requerimientos ante la DIAN', desc: 'Gestión de solicitudes, respuestas y trámites especiales ante entidades.' },
];

const AsesoriaTributariaPage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(heroScroll, [0, 0.8], [1, 0]);
  const heroScale = useTransform(heroScroll, [0, 1], [1, 1.1]);
  const [firstBlogSlug, setFirstBlogSlug] = useState(null);

  useEffect(() => {
    fetch(`${API}/blogs/como-lidiar-con-la-declaracion-en-la-vida-adulta`)
      .then(r => r.ok ? r.json() : null)
      .then(blog => { if (blog) setFirstBlogSlug(blog.slug || blog.id); })
      .catch(() => {});
  }, []);

  return (
    <div style={{ background: BG }} className="text-white" data-testid="tributaria-page">
      <style>{`@keyframes scrollPulse { 0%, 100% { opacity: 1; transform: translateY(0); } 50% { opacity: 0.3; transform: translateY(4px); } }`}</style>

      {/* Hero */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden">
        <motion.div className="absolute inset-0" style={{ scale: heroScale, background: BG }}>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `radial-gradient(circle at 30% 50%, ${PRIMARY} 0%, transparent 50%), radial-gradient(circle at 70% 80%, ${ACCENT} 0%, transparent 40%)` }} />
        </motion.div>
        <motion.div className="relative z-10 max-w-4xl mx-auto px-4 text-center" style={{ opacity: heroOpacity }}>
          <Link to="/servicios" className="inline-flex items-center text-gray-500 hover:text-white mb-8 transition-colors text-sm" data-testid="back-to-services"><ArrowLeft className="mr-2" size={16} /> Servicios</Link>
          <motion.p className="text-sm tracking-[0.3em] uppercase mb-6 font-medium" style={{ color: PRIMARY }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>Asesoría Tributaria</motion.p>
          <motion.h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 sm:mb-8 px-2" style={{ fontFamily: 'Playfair Display, serif' }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}>
            No aparecemos solo en <span className="italic" style={{ color: ACCENT }}>julio</span>
          </motion.h1>
          <motion.p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed px-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
            Te acompañamos todo el año para que cumplas con tus obligaciones fiscales sin estrés, con claridad y con un plan.
          </motion.p>
        </motion.div>
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.5, ease: 'easeInOut' }}><ChevronDown size={24} style={{ color: `${PRIMARY}99` }} /></motion.div>
      </section>

      <CinematicScrollWrapper />

      {/* Qué hace un buen contador */}
      <section className="py-16 sm:py-24 md:py-32 relative">
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${BG}, #0c1018, ${BG})` }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeSection>
            <p className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-3 sm:mb-4 text-center font-medium" style={{ color: PRIMARY }}>Lo que nos diferencia</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              ¿Qué hace un buen contador <span style={{ color: ACCENT }}>para ti?</span>
            </h2>
            <p className="text-gray-500 text-center mb-10 sm:mb-16 max-w-xl mx-auto text-sm sm:text-base">No aparece solo en julio. Te da claridad y tranquilidad fiscal todo el año.</p>
          </FadeSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { title: 'Te explica la normativa en términos simples', desc: 'No te deja con jerga legal que no entiendes. Te cuenta qué aplica para ti y por qué.' },
              { title: 'Anticipa cambios que te afectan', desc: 'Está al tanto de reformas tributarias y te avisa antes de que te tomen por sorpresa.' },
              { title: 'Identifica beneficios y deducciones reales', desc: 'Te ayuda a pagar lo justo — ni más, ni menos — usando todo lo que la ley permite.' },
              { title: 'Es honesto sobre lo que se puede y no hacer', desc: 'Sin atajos riesgosos. Te guía por el camino legal, incluso cuando no es el más fácil.' },
            ].map((item, i) => (
              <FadeSection key={i} delay={i * 0.05}>
                <div className="group p-6 rounded-2xl border transition-all duration-500 hover:scale-[1.01]" style={{ background: `${PRIMARY}08`, borderColor: `${PRIMARY}20` }}>
                  <h4 className="text-white font-semibold mb-2 text-base">{item.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section className="py-16 sm:py-24 md:py-32 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" style={{ background: `${PRIMARY}08` }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeSection>
            <p className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ color: ACCENT }}>Nuestro equipo contable</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-10 sm:mb-16 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Servicios <span style={{ color: PRIMARY }}>que ofrecemos</span>
            </h2>
          </FadeSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICIOS.map((s, i) => {
              const Icon = s.icon;
              return (
                <FadeSection key={i} delay={i * 0.05}>
                  <div className="group p-5 rounded-2xl border transition-all duration-500 hover:scale-[1.02]" style={{ background: `${BG}`, borderColor: `${PRIMARY}15` }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-colors" style={{ background: `${PRIMARY}15` }}>
                      <Icon className="w-5 h-5" style={{ color: PRIMARY }} />
                    </div>
                    <h4 className="text-white font-semibold mb-1.5 text-sm">{s.title}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">{s.desc}</p>
                  </div>
                </FadeSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-24 relative">
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${PRIMARY}12 0%, transparent 40%, ${ACCENT}08 100%)` }} />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <FadeSection>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              ¿Listo para dar el <span style={{ color: PRIMARY }}>primer paso?</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg mb-8 sm:mb-10 max-w-xl mx-auto">
              Elige cómo quieres empezar. Con un asesor o leyendo nuestro blog.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://wa.me/573208394000?text=Hola%2C%20me%20interesa%20la%20asesor%C3%ADa%20tributaria" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-300 hover:scale-105 w-full sm:w-auto justify-center" style={{ background: PRIMARY }} data-testid="tributaria-contact-cta">
                <MessageCircle size={20} /> Contactar Asesor
              </a>
              <Link to={firstBlogSlug ? `/blog/${firstBlogSlug}` : '/blog'} className="inline-flex items-center gap-3 border-2 px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-300 hover:scale-105 w-full sm:w-auto justify-center" style={{ borderColor: `${ACCENT}60`, color: ACCENT }} data-testid="tributaria-blog-cta">
                <BookOpen size={20} /> Ir al Blog
              </Link>
            </div>
          </FadeSection>
        </div>
      </section>
    </div>
  );
};

export default AsesoriaTributariaPage;
